/**
 * TipTapContentRenderer — Read-only renderer for TipTap JSON content.
 *
 * Uses a non-editable TipTap editor instance with the same extensions as the
 * ArtistProfileContentEditor so that rendering is pixel-perfect identical.
 */
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import ImageResize from 'tiptap-extension-resize-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Typography } from '@tiptap/extension-typography';
import YoutubeExtension from '@tiptap/extension-youtube';

interface TipTapContentRendererProps {
  /** JSON string of TipTap content (the `publishedContent` field). */
  content: string;
}

export default function TipTapContentRenderer({ content }: TipTapContentRendererProps) {
  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = JSON.parse(content);
  } catch {
    // If the content is not valid JSON, skip rendering
    return null;
  }

  if (!parsed) return null;

  return <TipTapReadonly content={parsed} />;
}

/** Inner component so useEditor runs unconditionally at the top level. */
function TipTapReadonly({ content }: { content: Record<string, unknown> }) {
  const editor = useEditor({
    editable: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        blockquote: {},
        horizontalRule: {},
        bulletList: {},
        orderedList: {},
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        HTMLAttributes: {
          class:
            'text-electric-cyan underline underline-offset-2 hover:text-electric-cyan/80 transition-colors cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      ImageResize.configure({
        inline: true,
        minWidth: 100,
        maxWidth: 800,
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: {
          class: 'border-collapse border border-white/10 w-full',
        },
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'border border-white/10',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-white/10 p-2 text-gray-300',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-white/10 p-2 bg-white/5 text-white font-semibold',
        },
      }),
      Typography,
      YoutubeExtension.configure({
        nocookie: true,
        controls: true,
        allowFullscreen: true,
        width: 640,
        height: 360,
        HTMLAttributes: {
          class: 'rounded-lg overflow-hidden my-4',
        },
      }),
    ],
    content,
    editorProps: {
      attributes: {
        class:
          'outline-none prose prose-invert max-w-none prose-headings:text-white prose-p:text-gray-300 prose-a:text-electric-cyan prose-strong:text-white prose-img:rounded-lg prose-img:max-w-full prose-blockquote:border-electric-cyan/40 prose-blockquote:text-gray-400 prose-hr:border-white/10',
      },
    },
  });

  if (!editor) return null;

  return <EditorContent editor={editor} />;
}
